import React, { useState, useEffect } from 'react';
import { Users, MapPin, User } from 'lucide-react';
import { formatValue } from '../../utils/formatters/valueUtils';
import PropertyCard from './ui/PropertyCard';
import DataItem from './ui/DataItem';
import RecordNavigator from './ui/RecordNavigator';

/**
 * Componente para visualização de dados do Sistema Nacional de Cadastro Rural (SNCR)
 * de uma propriedade rural, seguindo padrões visuais e utilizando utilitários compartilhados.
 */
const SncrTab = ({ propertyData, onHighlightGeometry }) => {
  // Estado para controle de índice do registro atual
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sncrDetails, setSncrDetails] = useState([]);

  // Log inicial para debug - removido conforme preferências do usuário

  // Validação de dados de entrada
  if (!propertyData?.properties?.sncr) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200">
        <div className="flex items-center text-gray-500">
          <Users className="mr-2" size={18} />
          <p>Nenhum dado SNCR disponível para esta propriedade.</p>
        </div>
      </div>
    );
  }

  // Normalização para garantir compatibilidade com diferentes formatos de dados
  // Verificamos se temos um array de registros SNCR ou um único registro
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const sncrData = propertyData.properties.sncr;

    // Se temos um array de dados SNCR, usamos ele
    if (Array.isArray(sncrData)) {
      // Array de registros SNCR
      setSncrDetails(sncrData);
    }
    // Se temos um objeto com campo 'details' que é array
    else if (sncrData.details && Array.isArray(sncrData.details)) {
      // Array details dentro do objeto SNCR
      setSncrDetails(sncrData.details);
    }
    // Se não é um array, tratamos como um único registro
    else {
      // SNCR como objeto único
      setSncrDetails([sncrData]);
    }
  }, [propertyData]);

  // Se não temos detalhes processados ainda
  if (!sncrDetails.length) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200">
        <div className="flex items-center text-gray-500">
          <Users className="mr-2" size={18} />
          <p>Processando dados SNCR...</p>
        </div>
      </div>
    );
  }

  // Obtemos o registro atual baseado no índice
  const sncrData = sncrDetails[currentIndex];

  // Função para destacar geometria no mapa (se implementado)
  const handleHighlight = () => {
    if (onHighlightGeometry && sncrData?.geojson) {
      onHighlightGeometry(sncrData);
    }
  };

  // Função para navegar entre registros SNCR
  const handleNavigate = (newIndex) => {
    if (newIndex >= 0 && newIndex < sncrDetails.length) {
      // Navega para outro registro SNCR
      setCurrentIndex(newIndex);
    }
  };

  return (
    <div className="p-4 bg-gray-50 rounded-lg">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center text-purple-700">
          <Users size={20} className="mr-2" />
          <h3 className="text-lg font-medium">Sistema Nacional de Cadastro Rural (SNCR)</h3>
        </div>

        {/* Componente de navegação se houver múltiplos registros */}
        {sncrDetails.length > 1 && (
          <RecordNavigator
            currentIndex={currentIndex}
            totalRecords={sncrDetails.length}
            onNavigate={handleNavigate}
          />
        )}
      </div>

      <div className="space-y-4">
        {/* Card principal com dados do imóvel */}
        <SncrPropertyCard data={sncrData} onHighlight={handleHighlight} />

        {/* Informações dos titulares, se disponíveis */}
        {sncrData.titulares &&
          Array.isArray(sncrData.titulares) &&
          sncrData.titulares.length > 0 && (
            <PropertyCard title="Titulares" icon={<Users className="text-purple-600" size={18} />}>
              {sncrData.titulares.map((titular, index) => (
                <SncrTitularCard key={index} data={titular} propertyData={propertyData} />
              ))}
            </PropertyCard>
          )}
      </div>
    </div>
  );
};

// Componente para exibir informações gerais do imóvel SNCR
const SncrPropertyCard = ({ data }) => {
  // Função para formatar área
  const formatArea = (value) => {
    if (value === undefined || value === null) return '--';
    const num = parseFloat(value);
    return isNaN(num) ? '--' : num.toFixed(2) + ' ha';
  };

  return (
    <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
      <h4 className="text-md font-medium mb-4 border-b pb-2">Informações do Imóvel</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DataItem label="Código do Imóvel" value={formatValue(data.codigo_incra)} />

        <DataItem label="Nome do Imóvel" value={formatValue(data.nome_da_propriedade)} />

        <DataItem
          icon={<MapPin size={16} />}
          label="Município"
          value={formatValue(data.município)}
        />

        <DataItem label="UF" value={formatValue(data.uf)} />

        <DataItem label="Área Total" value={formatArea(data.área_da_propriedade)} />
      </div>
    </div>
  );
};

// Componente para exibir informações de um titular SNCR
const SncrTitularCard = ({ data, propertyData }) => {
  // Estado para controlar se os dados estão revelados ou não
  const [isRevealed, setIsRevealed] = useState(false);
  // Estado para armazenar os dados não mascarados encontrados
  const [revealedData, setRevealedData] = useState(null);
  // Estado para controlar se existe uma entidade correspondente
  const [hasMatchingEntity, setHasMatchingEntity] = useState(false);

  // Função para normalizar entidades (adaptada de EntidadesTab)
  const normalizeEntity = (raw) => {
    if (!raw || typeof raw !== 'object') return null;

    // Detecta tipo por prioridade: campo explícito, tipo_documento, tamanho do documento
    const tipoDocumento = (raw.tipo_documento || raw.tipoDocumento || '').toUpperCase();
    let tipo =
      raw.tipo || (tipoDocumento === 'CPF' ? 'PF' : tipoDocumento === 'CNPJ' ? 'PJ' : null) || null;
    const doc = raw.documento || raw.cadastro?.ni || '';
    if (!tipo) {
      const onlyDigits = (doc || '').replace(/\D/g, '');
      if (onlyDigits.length === 11) tipo = 'PF';
      else if (onlyDigits.length === 14) tipo = 'PJ';
    }
    if (!tipo) tipo = 'PJ';

    if (tipo === 'PJ') {
      // Mapeia para cadastro (snake_case)
      const cadastro = {
        ni: doc || raw?.cadastral?.ni || '',
        tipo_estabelecimento: raw.tipoEstabelecimento || raw.cadastro?.tipo_estabelecimento,
        nome_empresarial: raw.nomeEmpresarial || raw.cadastro?.nome_empresarial,
        nome_fantasia: raw.nomeFantasia || raw.cadastro?.nome_fantasia,
        data_abertura: raw.dataAbertura || raw.cadastro?.data_abertura,
        natureza_juridica: raw.naturezaJuridica || raw.cadastro?.natureza_juridica,
      };
      return { tipo: 'PJ', cadastro, categoria: raw.categoria || [] };
    } else {
      // PF (quando existir). Mantém compatibilidade se já vier em 'cadastro'
      const cadastro = raw.cadastro || {
        nome: raw.nome || raw.nomeCompleto,
        ni: doc,
        nascimento: raw.dataNascimento || raw.nascimento,
        situacao_descricao: raw.situacao?.descricao || raw.situacao_descricao,
        data_inscricao: raw.dataInscricao || raw.data_inscricao,
      };
      return { tipo: 'PF', cadastro, categoria: raw.categoria || [] };
    }
  };

  // Função para formatar percentual
  const formatPercentual = (value) => {
    if (value === undefined || value === null) return '--';
    const num = parseFloat(value);
    return isNaN(num) ? '--' : num.toFixed(2) + '%';
  };

  // Função para formatar CPF
  const formatCPF = (cpf) => {
    if (!cpf) return '--';
    cpf = cpf.replace(/\D/g, '');
    if (cpf.length !== 11) return cpf;
    return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  };

  // Função para formatar CNPJ
  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '--';
    cnpj = cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14) return cnpj;
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  // Função para determinar e formatar documento
  const formatDocument = (doc) => {
    if (!doc) return '--';
    doc = doc.replace(/\D/g, '');
    if (doc.length === 11) return formatCPF(doc);
    if (doc.length === 14) return formatCNPJ(doc);
    return doc;
  };

  // Função para verificar se um nome está mascarado
  const isNameMasked = (name) => {
    if (!name) return false;
    // Considera mascarado se tiver asteriscos
    return name.includes('*');
  };

  // Função para extrair a parte não mascarada de um nome
  const getUnmaskedNamePart = (maskedName) => {
    if (!maskedName) return '';
    // Extrai a parte inicial até o primeiro espaço (primeiro nome)
    // ou até o primeiro asterisco, o que vier primeiro
    const firstAsteriskIndex = maskedName.indexOf('*');
    const firstSpaceIndex = maskedName.indexOf(' ');

    // Se temos ambos, pegamos o menor índice (primeiro que aparece)
    if (firstAsteriskIndex !== -1 && firstSpaceIndex !== -1) {
      return maskedName.substring(0, Math.min(firstAsteriskIndex, firstSpaceIndex)).trim();
    }
    // Se só temos asterisco
    else if (firstAsteriskIndex !== -1) {
      return maskedName.substring(0, firstAsteriskIndex).trim();
    }
    // Se só temos espaço
    else if (firstSpaceIndex !== -1) {
      return maskedName.substring(0, firstSpaceIndex).trim();
    }

    return maskedName.trim();
  };

  // Verifica se existe uma entidade com o mesmo início de nome
  useEffect(() => {
    // Verifica se o nome do titular está mascarado
    const nomeTitular = formatValue(data.titular);
    if (!isNameMasked(nomeTitular)) return;

    // Extrai a parte não mascarada do nome
    const unmaskedNamePart = getUnmaskedNamePart(nomeTitular);
    if (!unmaskedNamePart) return;

    // Busca nas entidades por uma correspondência
    // Garantir que estamos usando a chave correta para ENTIDADES (case insensitive)
    const props = propertyData?.properties || {};
    const entidadesKey = Object.keys(props).find((k) => k.toLowerCase() === 'entidades');
    const entidadesBruto = entidadesKey ? props[entidadesKey] : [];

    // Normalizar entidades da mesma forma que o EntidadesTab faz
    const list = Array.isArray(entidadesBruto)
      ? entidadesBruto
      : entidadesBruto
        ? [entidadesBruto]
        : [];
    const entidadesNormalizadas = list.map(normalizeEntity).filter(Boolean);

    // Buscar correspondência com os dados normalizados
    const matchingEntity = entidadesNormalizadas.find((entidade) => {
      const nomePF = entidade.cadastro?.nome;
      const nomePJ = entidade.cadastro?.nome_empresarial;
      const nome = nomePF || nomePJ || '';

      // Comparação case-insensitive mais flexível (includes)
      return nome.toLowerCase().includes(unmaskedNamePart.toLowerCase());
    });

    if (matchingEntity) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasMatchingEntity(true);
      // Armazena os dados revelados
      setRevealedData({
        nome: matchingEntity.cadastro?.nome || matchingEntity.cadastro?.nome_empresarial || '',
        documento: matchingEntity.cadastro?.ni || '',
      });
    } else {
      setHasMatchingEntity(false);
    }
  }, [data.titular, propertyData]);

  // Faz o toggle da revelação de dados
  const toggleReveal = () => {
    setIsRevealed(!isRevealed);
  };

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
      <div className="flex items-center mb-2">
        <User size={16} className="text-gray-600 mr-2" />
        <h4 className="text-md font-medium">{formatValue(data.titular) || 'Titular'}</h4>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex items-center">
          <DataItem
            label="Nome"
            value={isRevealed && revealedData ? revealedData.nome : formatValue(data.titular)}
          />
          {hasMatchingEntity && (
            <button
              onClick={toggleReveal}
              className="p-1 ml-2 rounded-full hover:bg-gray-100 bg-blue-50"
              title={isRevealed ? 'Mascarar dados sensíveis' : 'Revelar dados completos do titular'}
            >
              {isRevealed ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-blue-600"
                >
                  <path d="m3 3 18 18" />
                  <path d="M10.584 10.587a2 2 0 0 0 2.828 2.83" />
                  <path d="M9.363 5.365A9.466 9.466 0 0 1 12 5c4 0 7.333 2.333 10 7a13.856 13.856 0 0 1-1.67 2.68" />
                  <path d="M6.9 6.9a9 9 0 0 0-2.9 2.1L2 12s3 5 10 5a9.74 9.74 0 0 0 3.6-.7" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-blue-600"
                >
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          )}
        </div>

        <DataItem
          label="CPF/CNPJ"
          value={
            isRevealed && revealedData
              ? formatDocument(revealedData.documento)
              : formatValue(data.cpf_cnpj)
          }
        />

        <DataItem label="Percentual" value={formatPercentual(data.percentual_de_detenção)} />

        <DataItem label="Tipo" value={formatValue(data.tipo_de_proprietário)} />

        <DataItem label="Natureza Jurídica" value={formatValue(data.natureza_jurídica)} />

        {data.país_de_origem && (
          <DataItem label="País de Origem" value={formatValue(data.país_de_origem)} />
        )}
      </div>
    </div>
  );
};

export default SncrTab;
